export interface BemerkungsBild {
  bildId: string;
  originalname: string;
  contentType: string;
  dateigroesse: number;
  breite: number;
  hoehe: number;
}

export const BILD_LIMITS = { anzahl: 10, einzeln: 5 * 1024 * 1024, gesamt: 20 * 1024 * 1024, pixel: 40_000_000 };
const BEMERKUNG_ABSCHNITTE = [
  'Ort des Termins:',
  'Datum des Termins:',
  'Teilnahme:',
  'Anlass des Termins: Planung/Beratung/Besonderes Vorkommnis etc.',
  'Dokumentation:',
  'Vereinbarungen / nächste Schritte (intern, extern):'
];
export const BEMERKUNG_VORLAGE = BEMERKUNG_ABSCHNITTE.map(abschnitt => `<p><strong>${abschnitt}</strong></p><p><br></p>`).join('');
export const BEMERKUNG_VORLAGE_TEXT = BEMERKUNG_ABSCHNITTE.join('\n\n') + '\n\n';
export const BILD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function textAlsHtml(text: string): string {
  return (text || '').split(/\r\n|\r|\n/).map(line => '<p>' + (line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br>') + '</p>').join('');
}

export function pruefeBildmenge(groessen: number[], vorkommen: number): void {
  if (vorkommen > BILD_LIMITS.anzahl) throw new Error('Eine Bemerkung darf höchstens 10 Bilder enthalten.');
  if (groessen.some(size => size <= 0 || size > BILD_LIMITS.einzeln)) throw new Error('Ein Bild darf höchstens 5 MiB groß sein und darf nicht leer sein.');
  if (groessen.reduce((a, b) => a + b, 0) > BILD_LIMITS.gesamt) throw new Error('Die Bilder dürfen zusammen höchstens 20 MiB groß sein.');
}

export function bildGroesse(value: string): number | null {
  return value && /^\d+(?:\.\d+)?$/.test(value) && Number.isFinite(+value) && +value > 0
    ? Math.max(1, Math.round(Math.min(+value, 2400))) : null;
}

/** Baut ausschließlich erlaubte Elemente auf; Quellen aus fremdem HTML werden nie übernommen. */
export function bereinigterInhalt(html: string, bildUrls = new Map<string, string>()): HTMLDivElement {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  const output = document.createElement('div');
  const tags = new Set(['P', 'DIV', 'BR', 'OL', 'UL', 'LI']);
  const verboten = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE']);
  function copy(source: Node, target: Node, format = { fett: false, kursiv: false, unterstrichen: false }) {
    if (source.nodeType === Node.TEXT_NODE) {
      for (const [tag, aktiv] of [['strong', format.fett], ['em', format.kursiv], ['u', format.unterstrichen]] as const) {
        if (!aktiv) continue;
        const element = document.createElement(tag);
        target.appendChild(element);
        target = element;
      }
      target.appendChild(document.createTextNode(source.textContent || ''));
      return;
    }
    if (!(source instanceof Element) || verboten.has(source.tagName)) return;
    if (source.tagName === 'IMG') {
      const id = source.getAttribute('data-bild-id')?.toLowerCase();
      if (!id || !BILD_ID.test(id)) return;
      const img = document.createElement('img');
      img.setAttribute('data-bild-id', id);
      img.alt = source.getAttribute('alt') || 'Bild zur Bemerkung';
      for (const name of ['width', 'height']) {
        const value = bildGroesse(source.getAttribute(name));
        if (value !== null) img.setAttribute(name, String(value));
      }
      const url = bildUrls.get(id);
      if (url?.startsWith('blob:')) img.src = url;
      target.appendChild(img);
      return;
    }
    const textFormat = {
      fett: format.fett || ['STRONG', 'B'].includes(source.tagName),
      kursiv: format.kursiv || ['EM', 'I'].includes(source.tagName),
      unterstrichen: format.unterstrichen || source.tagName === 'U'
    };
    if (source instanceof HTMLElement) {
      // Nur unterstützte Textformatierungen aus der Zwischenablage übernehmen, niemals das CSS selbst.
      const style = source.style;
      const gewicht = style.fontWeight;
      if (gewicht === 'normal' || gewicht === 'lighter' || /^\d+(?:\.\d+)?$/.test(gewicht))
        textFormat.fett = Number(gewicht) >= 600;
      else if (gewicht === 'bold' || gewicht === 'bolder') textFormat.fett = true;
      if (style.fontStyle === 'normal') textFormat.kursiv = false;
      else if (style.fontStyle === 'italic' || style.fontStyle.startsWith('oblique')) textFormat.kursiv = true;
      const decoration = style.textDecorationLine || style.textDecoration;
      if (decoration.split(/\s+/).includes('underline')) textFormat.unterstrichen = true;
    }
    let next = target;
    if (tags.has(source.tagName)) {
      let tag = source.tagName.toLowerCase();
      if (tag === 'ol' && source.children.length && Array.from(source.children).every(x => x.getAttribute('data-list') === 'bullet')) tag = 'ul';
      next = document.createElement(tag);
      target.appendChild(next);
    }
    source.childNodes.forEach(child => copy(child, next, textFormat));
  }
  template.content.childNodes.forEach(node => copy(node, output));
  return output;
}

export function bildReferenzen(html: string): string[] {
  return Array.from(bereinigterInhalt(html).querySelectorAll('img')).map(img => img.getAttribute('data-bild-id'));
}

export function fehlermeldung(error: any): string {
  if (error?.status === 409) return 'Die Bemerkung wurde zwischenzeitlich geändert. Ihr Entwurf bleibt erhalten. Laden Sie den aktuellen Stand erst neu, wenn Sie Ihren Entwurf nicht mehr benötigen.';
  if (error?.status === 413) return 'Die Bemerkung ist für die Übertragung zu groß. Bitte reduzieren Sie die Bildgröße.';
  return error?.error?.msg || error?.error?.message || error?.message || 'Die Aktion ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
}
